"use client";

/**
 * dojo.tsx — 「灯下道场」共享视觉基元
 *
 * 与 /train 同一盏灯:绿炭毡底 + 头顶暖金灯 + 暗角。
 * 总览页(服务端组件)与场景库(客户端组件)共用同一套底框与动效,
 * 保证整个 Studio 是一件产品,不是三张皮。
 *
 * 动效纪律:只动 transform / opacity;prefers-reduced-motion 全部塌缩为静态。
 */

import { useEffect, useMemo } from "react";
import {
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from "motion/react";
import type { Variants } from "motion/react";
import { cn } from "@/lib/cn";

export const DISPLAY_FONT =
  "font-['Avenir_Next_Condensed','DIN_Alternate',Futura,'PingFang_SC',sans-serif]";

export const DOJO_EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

/** 道场底框:冲出 shell 的 p-6/md:p-8,铺满整个主区 */
export function DojoFrame({
  children,
  width = "max-w-6xl",
}: {
  children: React.ReactNode;
  width?: string;
}) {
  return (
    <div className="relative -m-6 min-h-[100dvh] overflow-hidden bg-[#090E0B] md:-m-8">
      {/* 环境光:头顶暖灯 + 牌桌毡光 + 四周暗角(与 train 页同参数) */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_75%_45%_at_50%_-8%,rgba(228,184,91,0.13),transparent_65%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_62%_50%_at_50%_36%,rgba(34,92,63,0.17),transparent_72%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_130%_100%_at_50%_55%,transparent_55%,rgba(0,0,0,0.55)_100%)]" />
      </div>
      {/* 矮视口(≤860px 高)上下留白收紧:1280×800 首屏须完整装下决策屏(r4 契约 item 17) */}
      <div className={cn(
        "relative mx-auto w-full px-5 py-10 md:px-8 md:py-12",
        width,
        "[@media(max-height:940px)]:py-6 [@media(max-height:940px)]:md:py-6",
      )}>
        {children}
      </div>
    </div>
  );
}

/** 交错入场容器:子项须为 <Rise>。翻页重挂时重新「发一手牌」。 */
export function Stagger({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const variants: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: reduce ? 0 : 0.07,
        delayChildren: reduce ? 0 : 0.05,
      },
    },
  };
  return (
    <motion.div
      className={className}
      variants={variants}
      initial="hidden"
      animate="visible"
    >
      {children}
    </motion.div>
  );
}

/** Stagger 的子项:自下而上落定。 */
export function Rise({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const variants: Variants = {
    hidden: { opacity: 0, y: reduce ? 0 : 18 },
    visible: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0.2 } : { duration: 0.5, ease: DOJO_EASE },
    },
  };
  return (
    <motion.div className={className} variants={variants}>
      {children}
    </motion.div>
  );
}

/** 悬停轻抬:牌卡被指尖挑起的手感。 */
export function Lift({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      whileHover={reduce ? undefined : { y: -4 }}
      transition={{ type: "spring", stiffness: 320, damping: 24 }}
    >
      {children}
    </motion.div>
  );
}

/** KPI 数字:开灯时从 0 数到目标值;reduced-motion 直接落定。 */
export function StatNumber({
  value,
  className,
}: {
  value: number;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const mv = useMotionValue(0);
  const text = useTransform(mv, (v) => String(Math.round(v)));

  useEffect(() => {
    if (reduce) {
      mv.set(value);
      return;
    }
    const controls = animate(mv, value, { duration: 0.9, ease: DOJO_EASE });
    return () => controls.stop();
  }, [mv, value, reduce]);

  return <motion.span className={cn("tabular-nums", className)}>{text}</motion.span>;
}

// ─── 空态引导(PLAN #17f5) ────────────────────────────────────────────────────
//
// 零数据/零输入页面的「第一步去哪」路标:≥3 张真实路由卡,禁假按钮。
// 复用于 /stats 零数据空态与 /coach 零输入空态;探针 probe/empty-state 以
// data-guide-card 选择器 fail-closed 断言卡数与可点性。
// (Turbopack 未重启期禁新建 src 文件,组件寄生于本已共享的 dojo.tsx。)

export type EmptyGuideCard = {
  href: string;
  title: string;
  desc: string;
  cta: string;
};

export function EmptyGuide({
  title,
  subtitle,
  cards,
  className,
}: {
  title: string;
  subtitle?: string;
  cards: EmptyGuideCard[];
  className?: string;
}) {
  return (
    <div
      data-empty-guide=""
      className={cn(
        "rounded-2xl border border-[#223329] bg-[#0D1510]/80 px-5 py-8 md:px-7",
        className,
      )}
    >
      <div className="text-center">
        <p className="text-sm font-medium text-stone-300">{title}</p>
        {subtitle && (
          <p className="mx-auto mt-2 max-w-[46ch] text-xs leading-relaxed text-stone-500">
            {subtitle}
          </p>
        )}
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        {cards.map((c) => (
          <a
            key={c.href}
            data-guide-card=""
            href={c.href}
            className="group flex flex-col rounded-xl border border-[#2A3D30] bg-[#0C130F]/80 px-4 py-4 transition hover:border-emerald-400/40 hover:bg-[#111B14] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/60"
          >
            <span className="text-sm font-bold text-stone-100 transition-colors group-hover:text-emerald-200">
              {c.title}
            </span>
            <span className="mt-1.5 flex-1 text-xs leading-relaxed text-stone-500">
              {c.desc}
            </span>
            <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-300">
              {c.cta}
              <span
                aria-hidden
                className="transition-transform duration-150 group-hover:translate-x-0.5"
              >
                →
              </span>
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}

// ─── 169 起手牌矩阵(PLAN #dab5 · r1 #4e12「零量化可视化」首刀) ────────────────
//
// 13×13 起手牌全域:对角线 = 口袋对,上三角 = 同花,下三角 = 杂色。格色 = Chen
// 公式分层热力 —— Chen 是公开的**纯牌面组合公式**(高牌分/对子翻倍/同花加分/
// 隔张惩罚/连张奖励),零扫描表数值、零 D-002 暴露。作答揭晓态高亮
// spot.hero_hand 对应格;hero_hand 缺失/不可解析 → 整体不渲染(禁编造)。
// (Turbopack 未重启期禁新建 src 文件,组件寄生于本已共享的 dojo.tsx;同 EmptyGuide 先例。)

export const MATRIX_RANKS = ["A", "K", "Q", "J", "T", "9", "8", "7", "6", "5", "4", "3", "2"] as const;
export type MatrixRank = (typeof MATRIX_RANKS)[number];

const RANK_INDEX: Record<string, number> = {};
MATRIX_RANKS.forEach((r, i) => {
  RANK_INDEX[r] = i;
});

/** Chen 公式高牌分:A=10 K=8 Q=7 J=6 T=5,其余 = 点数 ÷ 2 */
const CHEN_HIGH: Record<string, number> = {
  A: 10, K: 8, Q: 7, J: 6, T: 5,
  "9": 4.5, "8": 4, "7": 3.5, "6": 3, "5": 2.5, "4": 2, "3": 1.5, "2": 1,
};

/** 纯组合:两张牌的 Chen 起手分(公共公式推得,非扫描数值) */
export function chenScore(hi: MatrixRank, lo: MatrixRank, pair: boolean, suited: boolean): number {
  let s = pair ? Math.max(5, CHEN_HIGH[hi] * 2) : CHEN_HIGH[hi];
  if (suited && !pair) s += 2;
  if (!pair) {
    const gap = RANK_INDEX[hi] - RANK_INDEX[lo];
    if (gap === 1) s -= 1;
    else if (gap === 2) s -= 2;
    else if (gap === 3) s -= 4;
    else if (gap >= 4) s -= 5;
    // 连张潜力:0/1 隔且两张都低于 Q
    if (gap <= 1 && RANK_INDEX[hi] > RANK_INDEX.Q) s += 1;
  }
  return s;
}

export type HandMatrixCell = {
  label: string; // "QQ" / "AKs" / "AKo"
  row: number;
  col: number;
  score: number; // Chen 公式分(公开起手强度公式,非扫描数值)
  tier: 1 | 2 | 3 | 4 | 5; // 1 最弱 → 5 最强
};

/** 全域 169 格(行 = 高牌 A→2,列 = 高牌 A→2;上三角同花/下三角杂色) */
export function buildHandMatrix169(): HandMatrixCell[] {
  const cells: HandMatrixCell[] = [];
  for (let row = 0; row < 13; row++) {
    for (let col = 0; col < 13; col++) {
      if (row === col) {
        const r = MATRIX_RANKS[row];
        const score = chenScore(r, r, true, false);
        cells.push({ label: `${r}${r}`, row, col, score, tier: tierOf(score) });
      } else if (row < col) {
        // 上三角:行牌较高 → 同花
        const hi = MATRIX_RANKS[row];
        const lo = MATRIX_RANKS[col];
        cells.push({ label: `${hi}${lo}s`, row, col, score: chenScore(hi, lo, false, true), tier: tierOf(chenScore(hi, lo, false, true)) });
      } else {
        // 下三角:列牌较高 → 杂色
        const hi = MATRIX_RANKS[col];
        const lo = MATRIX_RANKS[row];
        cells.push({ label: `${hi}${lo}o`, row, col, score: chenScore(hi, lo, false, false), tier: tierOf(chenScore(hi, lo, false, false)) });
      }
    }
  }
  return cells;
}

function tierOf(score: number): 1 | 2 | 3 | 4 | 5 {
  if (score >= 12) return 5;
  if (score >= 9) return 4;
  if (score >= 8) return 3;
  if (score >= 6.5) return 2;
  return 1;
}

/**
 * hero_hand → 矩阵格 label。吃两种记法:
 * ① 具体两张牌 "AhKd"/"7h6h"(现行 220/220 train 场景全为此形);
 * ② 牌域记法 "AKs"/"AKo"/"99"。不可解析/无法定花色关系 → null(调用方不渲染,禁编造)。
 */
export function heroCellLabel(heroHand: string | null | undefined): string | null {
  if (!heroHand) return null;
  const t = heroHand.trim().toUpperCase().replace(/10/g, "T");
  // ① 具体两张牌:rank+suit ×2
  if (/^(?:[2-9TJQKA][SHDC]){2}$/.test(t)) {
    const cs = [...t.matchAll(/([2-9TJQKA])([SHDC])/g)];
    const a = cs[0];
    const b = cs[1];
    if (!a || !b) return null;
    const r1 = a[1];
    const r2 = b[1];
    if (!r1 || !r2) return null;
    const i1 = RANK_INDEX[r1];
    const i2 = RANK_INDEX[r2];
    if (i1 === undefined || i2 === undefined) return null;
    if (i1 === i2) return `${r1}${r1}`;
    const hi = i1 < i2 ? r1 : r2;
    const lo = i1 < i2 ? r2 : r1;
    const suited = a[2] === b[2];
    return `${hi}${lo}${suited ? "s" : "o"}`;
  }
  // ② 牌域记法:rank rank [s|o]
  const m = /^([2-9TJQKA])([2-9TJQKA])([SO])$/.exec(t);
  const p = /^([2-9TJQKA])\1$/.exec(t);
  if (p) return `${p[1]}${p[1]}`;
  if (!m) return null;
  if (RANK_INDEX[m[1]] === undefined || RANK_INDEX[m[2]] === undefined) return null;
  const i1 = RANK_INDEX[m[1]];
  const i2 = RANK_INDEX[m[2]];
  if (i1 === i2) return `${m[1]}${m[1]}`;
  const hi = i1 < i2 ? m[1] : m[2];
  const lo = i1 < i2 ? m[2] : m[1];
  return `${hi}${lo}${m[3] === "S" ? "s" : "o"}`;
}

const TIER_STYLE: Record<HandMatrixCell["tier"], string> = {
  5: "bg-emerald-400/80 text-[#062015]",
  4: "bg-emerald-500/50 text-emerald-50",
  3: "bg-amber-400/35 text-amber-50",
  2: "bg-stone-500/40 text-stone-200",
  1: "bg-stone-700/60 text-stone-400",
};

/** 169 格起手牌全图:揭晓态把你的手牌放回全域,颜色越绿起手越强,金格 = 你这手 */
export function HandMatrix169({
  heroHand,
  className,
}: {
  heroHand: string | null | undefined;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const cells = useMemo(() => buildHandMatrix169(), []);
  const hero = heroCellLabel(heroHand);
  if (!hero) return null;
  return (
    <div
      data-hand-matrix="169"
      data-hero-hand={heroHand ?? ""}
      role="img"
      aria-label={`169 格起手牌全图:颜色越绿起手越强,你这手 ${hero} 是金色的那一格`}
      className={cn("rounded-xl border border-[#223329] bg-[#0B120E] p-3", className)}
    >
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-[11px] font-medium tracking-wider text-stone-500">
          起手牌全图
        </span>
        <span className={cn("font-mono text-[11px] font-bold tracking-tight text-amber-200")}>
          你这手 {hero}
        </span>
      </div>
      <div className="grid grid-cols-[repeat(13,minmax(0,1fr))] gap-[2px]">
        {cells.map((c) =>
          c.label === hero ? (
            <motion.span
              key={c.label}
              data-cell={c.label}
              data-hero-cell={c.label}
              className="relative z-10 flex aspect-square items-center justify-center rounded-[3px] bg-amber-300 font-mono text-[8px] font-extrabold leading-none text-[#3A2A05] ring-2 ring-amber-200 shadow-[0_0_10px_rgba(251,191,36,0.6)] md:text-[10px]"
              initial={reduce ? false : { scale: 0.35, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={
                reduce
                  ? { duration: 0 }
                  : { type: "spring", stiffness: 380, damping: 24, delay: 0.12 }
              }
            >
              {c.label}
            </motion.span>
          ) : (
            <span
              key={c.label}
              data-cell={c.label}
              className={cn(
                "flex aspect-square items-center justify-center rounded-[3px] font-mono text-[7px] font-semibold leading-none opacity-80 md:text-[9px]",
                TIER_STYLE[c.tier],
              )}
            >
              {c.label}
            </span>
          ),
        )}
      </div>
      <p className="mt-2 text-[10px] leading-relaxed text-stone-500">
        越绿越强,越灰越弱;金色一格 = 你现在拿的这手牌
      </p>
    </div>
  );
}

/** 纯组合:一个牌域格的组合数(对子 6 / 同花 4 / 杂色 12) */
export function combosForCell(label: string): number {
  if (/^[2-9TJQKA]{2}$/.test(label)) return 6;
  if (label.endsWith("s")) return 4;
  if (label.endsWith("o")) return 12;
  return 0;
}

export const TOTAL_COMBOS = 1326;

/** 纯组合:各强度层占全部 1326 种起手组合的份额(降序 tier5→tier1) */
export function tierComboShares(): { tier: HandMatrixCell["tier"]; combos: number; frac: number }[] {
  const byTier = new Map<HandMatrixCell["tier"], number>();
  for (const c of buildHandMatrix169()) {
    byTier.set(c.tier, (byTier.get(c.tier) ?? 0) + combosForCell(c.label));
  }
  const rows = ([5, 4, 3, 2, 1] as const).map((tier) => {
    const combos = byTier.get(tier) ?? 0;
    return { tier, combos, frac: combos / TOTAL_COMBOS };
  });
  return rows;
}

/**
 * 纯组合:hero 牌域在 1326 种起手组合中的百分位(公开 Chen 公式,非扫描数值)。
 * midrank 口径:更强国 + 同分半数,再 ÷ 1326。0 = 全场最强,100 = 全场最弱。
 * label 不可解析 → null(调用方不渲染刻度,禁编造)。
 */
export function chenPercentile(label: string | null | undefined): number | null {
  const cells = buildHandMatrix169();
  const hero = cells.find((c) => c.label === label);
  if (!hero) return null;
  let stronger = 0;
  let ties = 0;
  for (const c of cells) {
    const n = combosForCell(c.label);
    if (c.score > hero.score) stronger += n;
    else if (c.score === hero.score) ties += n;
  }
  return ((stronger + ties / 2) / TOTAL_COMBOS) * 100;
}

const FREQ_TIER_BG: Record<HandMatrixCell["tier"], string> = {
  5: "bg-emerald-300/90",
  4: "bg-emerald-500/60",
  3: "bg-amber-400/45",
  2: "bg-stone-500/50",
  1: "bg-stone-700/70",
};

const TIER_ZH: Record<HandMatrixCell["tier"], string> = {
  5: "很强",
  4: "强",
  3: "中",
  2: "偏弱",
  1: "弱",
};

/**
 * 起手强度频率条(#937b/#4e12 量化可视化第三刀):1326 种起手组合按公开公式分五层
 * 堆叠成一根频率条,金色刻度 = 你这手牌的百分位(越靠左越强)。纯组合数学,零扫描数值。
 */
export function FreqBar169({
  heroHand,
  className,
}: {
  heroHand: string | null | undefined;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const segs = useMemo(() => tierComboShares(), []);
  const hero = heroCellLabel(heroHand);
  const pct = hero ? chenPercentile(hero) : null;
  if (!hero || pct === null) return null;
  return (
    <div
      data-freq-bar="169"
      data-hero-hand={heroHand ?? ""}
      role="img"
      aria-label={`起手强度频率条:1326 种起手组合分层堆叠,你这手 ${hero} 在前 ${Math.round(pct)}% 处(公开公式估算)`}
      className={cn("rounded-xl border border-[#223329] bg-[#0B120E] p-3", className)}
    >
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-[11px] font-medium tracking-wider text-stone-500">
          起手强度频率条
        </span>
        <span className="font-mono text-[11px] font-bold tracking-tight text-amber-200">
          {hero} ≈ 前 {Math.round(pct)}%
        </span>
      </div>
      <div className="relative h-5 overflow-hidden rounded-full ring-1 ring-inset ring-[#223329]">
        <div className="flex h-full w-full">
          {segs.map((s, i) => (
            <motion.div
              key={s.tier}
              data-freq-seg={s.tier}
              data-freq-frac={s.frac.toFixed(4)}
              className={cn("h-full", FREQ_TIER_BG[s.tier])}
              style={{ width: `${s.frac * 100}%` }}
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={reduce ? { duration: 0 } : { duration: 0.26, delay: 0.06 * i }}
            />
          ))}
        </div>
        <motion.span
          data-hero-marker={hero}
          data-hero-pct={pct.toFixed(1)}
          className="absolute inset-y-0 w-[3px] -translate-x-1/2 rounded-full bg-amber-300 shadow-[0_0_8px_rgba(251,191,36,0.8)]"
          style={{ left: `${pct}%` }}
          initial={reduce ? false : { opacity: 0, scaleY: 0.4 }}
          animate={{ opacity: 1, scaleY: 1 }}
          transition={
            reduce ? { duration: 0 } : { type: "spring", stiffness: 340, damping: 22, delay: 0.18 }
          }
        />
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[9px] text-stone-500">
        <span className="flex items-center gap-2">
          {segs.map((s) => (
            <span key={s.tier} className="flex items-center gap-1">
              <span className={cn("inline-block size-1.5 rounded-[2px]", FREQ_TIER_BG[s.tier])} />
              {TIER_ZH[s.tier]}
            </span>
          ))}
        </span>
        <span>越左越强 · 1326 种组合</span>
      </div>
    </div>
  );
}
